import { throttle } from '../utils/eventUtils';

export class DragDropElements {
  static containerInstances = new Set();
  static placeholder = null;
  static draggedElement = null;
  static disabled = false;
  static isDropped = false;
  static onEndDrop = null;
  static draggedElementSourceContainer = null;
  static deleteZoneContainer = null; // Контейнер удаления
  static dragEnterCounter = 0; // Счётчик входов в контейнер удаления

  constructor(container) {
    if (!container) throw new Error('Container is required');
    this.container = container;

    if (DragDropElements.containerInstances.has(container)) {
      return;
    } else {
      DragDropElements.containerInstances.add(container);
      container.dragDropInstance = this;
    }

    this.log = {
      handleMouseUp: false,
      handleMousedown: false,
      handleDragStart: false,
      handleDragEnd: false,
      handleDragEndDocument: false,
      handleDragOver: false,
      handleDragEnter: false,
      handleDragLeave: false,
      handleDrop: false,
      initContainer: false,
      initDraggableElements: false,
      findClosestDragContainer: false,
      getClosestElement: false,
      getInstanceForContainer: false
    };

    this.handleDragEndDocument = this.handleDragEndDocument.bind(this);
    this.initContainer();
    this.initDraggableElements();
  }

  logMessage(functionName, message) {
    if (this.log[functionName]) {
      console.log(`[${functionName}]`, message);
    }
  }

  initContainer() {
    this.logMessage('initContainer', 'Initializing container');
    this.throttledHandleDragOverProcessor = throttle(this.handleDragOverProcessor.bind(this), 50, false);

    this.handleMouseUp = this.handleMouseUp.bind(this); // Привязка контекста
    this.container.addEventListener('mousedown', this.handleMousedown.bind(this));
    this.container.addEventListener('dragover', this.handleDragOver.bind(this));
    this.container.addEventListener('drop', this.handleDrop.bind(this));

    // Добавляем обработчики для контейнера удаления
    if (this.container.dataset.draggableDelete === 'true') {
      this.container.addEventListener('dragenter', this.handleDragEnter.bind(this));
      this.container.addEventListener('dragleave', this.handleDragLeave.bind(this));
    }

    this.observeDynamicDraggableElements();
  }

  handleMouseUp() {
    this.logMessage('handleMouseUp', 'Mouse up detected');
    this.stopDraging = false;
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  handleMousedown(event) {
    this.logMessage('handleMousedown', `Mousedown on target: ${event.target}`);
    const target = event.target;

    if (target.classList.contains('btn') || target.closest('input') || target.closest('.nodraggable') || target.closest('textarea') || target.isContentEditable) {
      this.stopDraging = true;
      document.addEventListener('mouseup', this.handleMouseUp);
    }
  }

  initializeDraggableElement(el) {
    if (!el.dataset.dragDropInitialized) {
      el.setAttribute('draggable', true);
      el.addEventListener('dragstart', this.handleDragStart.bind(this));
      el.addEventListener('dragend', this.handleDragEnd.bind(this));
      el.dataset.dragDropInitialized = true;
    }
  }

  observeDynamicDraggableElements() {
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains('draggable')) {
              this.initializeDraggableElement(node);
            }
          });
        }
      }
    });

    observer.observe(this.container, { childList: true, subtree: true });
  }

  initDraggableElements(element) {
    this.logMessage('initDraggableElements', 'Initializing draggable elements');
    const elements = element ? [element] : this.container.querySelectorAll('.draggable');
    elements.forEach((el) => this.initializeDraggableElement(el));
  }

  handleDragStart(event) {
    // document.body.style.overflow = 'hidden'; // Блокировка прокрутки
    this.logMessage('handleDragStart', `Drag start for target: ${event.target} stopDraging ${this.stopDraging} disable ${this.disable}`);
    if (this.stopDraging || this.disable) {
      event.preventDefault();
      return;
    }

    const target = event.target;

    // Проверка: цель существует
    if (!target) {
      event.preventDefault();
      this.logMessage('handleDragStart', 'Перетаскивание невозможно: цель отсутствует.');
      return;
    }
    const scrollOffset = target.offsetHeight;
    const distanceToBottom = document.documentElement.scrollHeight - window.scrollY - window.innerHeight;

    if (distanceToBottom < scrollOffset) {
      window.scrollBy(0, -scrollOffset);
    }

    // Проверка запрещённых классов и тегов
    if (target.classList.contains('btn') || target.closest('input') || target.closest('textarea') || target.isContentEditable) {
      event.preventDefault();
      this.logMessage('handleDragStart', 'Перетаскивание невозможно: запрещённый элемент.');
      return;
    }

    // Проверка: элемент draggable
    if (!target.draggable) {
      event.preventDefault();
      this.logMessage('handleDragStart', 'Перетаскивание невозможно: элемент не draggable.');
      return;
    }

    // Проверка: поддерживается ли перетаскивание
    if (!event.dataTransfer) {
      event.preventDefault();
      this.logMessage('handleDragStart', 'Перетаскивание невозможно: отсутствует dataTransfer.');
      return;
    }

    // Инициализация переменных для DragDrop
    DragDropElements.draggedElement = target;
    DragDropElements.draggedElementSourceContainer = this.container;
    const container = DragDropElements.draggedElementSourceContainer;

    // Проверка: контейнер существует
    if (!container) {
      event.preventDefault();
      this.logMessage('handleDragStart', 'Перетаскивание невозможно: контейнер отсутствует.');
      return;
    }

    // Проверка: элемент внутри таблицы
    if (DragDropElements.draggedElement.closest('tbody')) {
      DragDropElements.placeholder = document.createElement('tr');
      const placeholderTd = document.createElement('td');
      placeholderTd.colSpan = 100; // или конкретное количество колонок
      placeholderTd.style.height = `${DragDropElements.draggedElement.offsetHeight}px`;
      DragDropElements.draggedElement.style.position = 'absolute';
      DragDropElements.draggedElement.style.opacity = 0.01;
      DragDropElements.placeholder.appendChild(placeholderTd);
    } else {
      // Создание placeholder
      DragDropElements.placeholder = document.createElement('div');
      DragDropElements.placeholder.className = 'placeholder';
      DragDropElements.placeholder.style.height = `${DragDropElements.draggedElement.offsetHeight}px`;
    }

    // Установка данных для dataTransfer
    try {
      event.dataTransfer.setData('text/plain', '');
    } catch (error) {
      this.logMessage('handleDragStart', `Ошибка установки dataTransfer: ${error.message}`);
      event.preventDefault();
      return;
    }

    // Добавляем обработчик dragend на document
    document.addEventListener('dragend', this.handleDragEndDocument);

    this.logMessage('handleDragStart', 'Перетаскивание успешно началось.');
  }

  handleDragEnd(event) {
    this.logMessage('handleDragEnd', 'Drag end');
    event.target.classList.remove('dragging');

    DragDropElements.draggedElement = null;
    DragDropElements.draggedElementSourceContainer = null;
    DragDropElements.placeholder?.remove();
    DragDropElements.placeholder = null;

    // Удаляем класс подсветки с контейнера удаления, если он есть
    if (DragDropElements.deleteZoneContainer) {
      DragDropElements.deleteZoneContainer.classList.remove('delete-zone');
      DragDropElements.deleteZoneContainer = null;
      DragDropElements.dragEnterCounter = 0;
    }
  }

  handleDragEndDocument(event) {
    this.logMessage('handleDragEndDocument', 'Drag end on document');
    // Снимаем все флаги и классы
    if (DragDropElements.deleteZoneContainer) {
      DragDropElements.deleteZoneContainer.classList.remove('delete-zone');
      DragDropElements.deleteZoneContainer = null;
      DragDropElements.dragEnterCounter = 0;
    }

    event.target.style.position = 'unset';
    event.target.style.opacity = 1;
    document.removeEventListener('dragend', this.handleDragEndDocument);
    if (this.onEndDrop) this.onEndDrop();
    // Обновление данных
    const fieldset = event.target.closest('fieldset');
    fieldset.sortUpdate();
    fieldset.hardUpdateData();
  }

  handleDragOverProcessor() {
    this.logMessage('handleDragOverProcessor', 'Drag over Processor');
    const container = this.findClosestDragContainer(event.target);
    if (!container) return;

    const isNoReorder = container.dataset.draggableNoReorder === 'true' && container === DragDropElements.draggedElementSourceContainer;

    const isDeleteMode = container.dataset.draggableDelete === 'true' && container !== DragDropElements.draggedElementSourceContainer;

    if (isDeleteMode || isNoReorder) {
      // Не переставляем внутри, не ставим плейсхолдер
      return;
    }

    const closest = this.getClosestElement(container, event.clientY);

    if (closest && closest !== DragDropElements.placeholder) {
      container.insertBefore(DragDropElements.placeholder, closest);
    } else if (!closest) {
      container.appendChild(DragDropElements.placeholder);
    }
  }

  handleDragOver(event) {
    this.logMessage('handleDragOver', 'Drag over');
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    this.throttledHandleDragOverProcessor();
  }

  handleDragEnter(event) {
    this.logMessage('handleDragEnter', 'Drag enter');
    event.preventDefault();
    const container = event.currentTarget;
    if (!DragDropElements.deleteZoneContainer) {
      DragDropElements.deleteZoneContainer = container;
    }
    DragDropElements.dragEnterCounter++;
    container.classList.add('delete-zone');
  }

  handleDragLeave(event) {
    this.logMessage('handleDragLeave', 'Drag leave');
    event.preventDefault();
    const container = event.currentTarget;
    DragDropElements.dragEnterCounter--;
    if (DragDropElements.dragEnterCounter <= 0) {
      container.classList.remove('delete-zone');
      DragDropElements.deleteZoneContainer = null;
      DragDropElements.dragEnterCounter = 0;
    }

    DragDropElements.draggedElement.style.position = 'unset';
    DragDropElements.draggedElement.style.opacity = 1;
  }

  handleDrop(event) {
    this.logMessage('handleDrop', 'Drop event');
    event.preventDefault();

    const container = this.findClosestDragContainer(event.target);
    if (!container || !DragDropElements.draggedElement) return;

    const isCopyMode = container.dataset.draggableCopy === 'true';
    const isRemoveMode = DragDropElements.draggedElementSourceContainer.dataset.draggableRemove === 'true';
    const isDeleteMode = container.dataset.draggableDelete === 'true' && container !== DragDropElements.draggedElementSourceContainer;
    const isNoReorder = container.dataset.draggableNoReorder === 'true' && container === DragDropElements.draggedElementSourceContainer;

    DragDropElements.draggedElement.style.position = 'unset';
    DragDropElements.draggedElement.style.opacity = 1;

    if (isNoReorder) {
      return;
    }

    if (isDeleteMode) {
      // Удаляем элемент
      if (DragDropElements.draggedElementSourceContainer.contains(DragDropElements.draggedElement)) {
        DragDropElements.draggedElementSourceContainer.removeChild(DragDropElements.draggedElement);
      }
    } else if (isCopyMode) {
      // Копирование элемента
      const clonedElement = DragDropElements.draggedElement.cloneNode(true);
      clonedElement.classList.remove('dragging');
      delete clonedElement.dataset.dragDropInitialized;
      // Важно: вместо appendChild заменяем плейсхолдер
      if (DragDropElements.placeholder && container.contains(DragDropElements.placeholder)) {
        container.replaceChild(clonedElement, DragDropElements.placeholder);
      } else {
        container.appendChild(clonedElement);
      }

      // Генерация нового уникального идентификатора
      const newUniqId = 'id_' + Date.now() + '_' + Math.floor(Math.random() * 100000);

      // Функция для обновления атрибутов 'name'
      function updateNameAttributes(element) {
        if (element.hasAttribute('name')) {
          const name = element.getAttribute('name');
          const updatedName = name.replace(/\$uniqid/g, newUniqId);
          element.setAttribute('name', updatedName);
        }
        // Рекурсивно обрабатываем дочерние элементы
        Array.from(element.children).forEach(updateNameAttributes);
      }

      // Обновление атрибутов 'name' в клонированном элементе
      updateNameAttributes(clonedElement);

      // Инициализация нового элемента
      let dragDropInstance = this.getInstanceForContainer(container);
      dragDropInstance.initDraggableElements(clonedElement);


      const inputs = clonedElement.querySelectorAll('input, textarea, .nodraggable');

      inputs.forEach((input) => {
        input.addEventListener('mouseenter', () => {
          // Сохраняем текущее значение draggable
          clonedElement.dataset.originalDraggable = clonedElement.getAttribute('draggable');
          // Отключаем draggable
          clonedElement.setAttribute('draggable', 'false');
        });

        input.addEventListener('mouseleave', () => {
          // Восстанавливаем значение draggable
          if (clonedElement.dataset.originalDraggable !== undefined) {
            clonedElement.setAttribute('draggable', clonedElement.dataset.originalDraggable);
            delete clonedElement.dataset.originalDraggable;
          }
        });
      });

      // Помечаем элемент как инициализированный

      if (isRemoveMode && container !== DragDropElements.draggedElementSourceContainer) {
        DragDropElements.draggedElementSourceContainer.removeChild(DragDropElements.draggedElement);
      }
    } else {
      // Перемещение элемента
      if (DragDropElements.placeholder && container.contains(DragDropElements.placeholder)) {
        container.replaceChild(DragDropElements.draggedElement, DragDropElements.placeholder);
      } else {
        container.appendChild(DragDropElements.draggedElement);
      }

      // Если контейнер изменился, перевешиваем события
      if (container !== DragDropElements.draggedElementSourceContainer) {
        DragDropElements.draggedElementSourceContainer.dispatchEvent(
          new CustomEvent('elementRemoved', {
            detail: DragDropElements.draggedElement
          })
        );
        let dragDropInstance = this.getInstanceForContainer(container);
        dragDropInstance.initDraggableElements(DragDropElements.draggedElement);
      }
    }

    // Удаляем класс подсветки с контейнера удаления, если он есть
    if (DragDropElements.deleteZoneContainer) {
      DragDropElements.deleteZoneContainer.classList.remove('delete-zone');
      DragDropElements.deleteZoneContainer = null;
      DragDropElements.dragEnterCounter = 0;
    }

    DragDropElements.placeholder?.remove();

    DragDropElements.placeholder = null;
    DragDropElements.draggedElement = null;
    DragDropElements.draggedElementSourceContainer = null;
  }

  findClosestDragContainer(target) {
    let current = target;
    while (current && !DragDropElements.containerInstances.has(current)) {
      current = current.parentElement;
    }
    return current;
  }

  getClosestElement(container, mouseY) {
    const children = [...container.children].filter((child) => child !== DragDropElements.placeholder && child !== DragDropElements.draggedElement);

    return children.reduce(
      (closest, child) => {
        const box = child.getBoundingClientRect();
        const offset = mouseY - box.top - box.height / 2;
        if (offset < 0 && offset > closest.offset) {
          return { offset, element: child };
        } else {
          return closest;
        }
      },
      { offset: Number.NEGATIVE_INFINITY }
    ).element;
  }

  getInstanceForContainer(container) {
    return container.dragDropInstance;
  }
}
